import { ColDef } from "ag-grid-community";
import { Box } from "@mui/material";
import { useMemo } from "react";
import { Typography } from "@platform/design-system";

import { CARD_SX } from "./constants";
import { fmtNumber } from "./formatters";
import { CardTitle } from "./primitives";
import { StewardshipTable } from "./StewardshipTable";

import type { RfpSubmitterActivity } from "../types";

type RfpSubmitterActivityCardProps = {
  rows: RfpSubmitterActivity[];
  // Gated on REMOVE_IGO by the caller: when false, the two IGO columns are
  // dropped. The fields still arrive on each row; only the columns are hidden.
  showIgoColumns: boolean;
};

// Average metrics can be absent; show an em dash rather than a blank/zero cell.
const metricFormatter = ({
  value,
}: {
  value: number | null | undefined;
}): string => (value == null ? "—" : String(value));

const numberFormatter = ({ value }: { value: number }): string =>
  fmtNumber(value);

function RfpSubmitterActivityCard({
  rows,
  showIgoColumns,
}: RfpSubmitterActivityCardProps): React.JSX.Element {
  const columnDefs = useMemo<ColDef<RfpSubmitterActivity>[]>(
    () => [
      { field: "submitter_name", headerName: "User", flex: 1.5 },
      {
        field: "rfp_count",
        headerName: "RFPs",
        type: "rightAligned",
        valueFormatter: numberFormatter,
      },
      {
        field: "avg_number_of_carriers",
        headerName: "Avg. Carriers",
        type: "rightAligned",
        valueFormatter: metricFormatter,
      },
      ...(showIgoColumns
        ? [
            {
              field: "avg_days_to_igo" as const,
              headerName: "Avg. Days to IGO",
              type: "rightAligned",
              valueFormatter: metricFormatter,
            },
          ]
        : []),
      {
        field: "avg_turnaround_time_business_days",
        headerName: "Avg. Turnaround",
        type: "rightAligned",
        valueFormatter: metricFormatter,
      },
      ...(showIgoColumns
        ? [
            {
              field: "avg_in_good_order_to_delivered_business_days" as const,
              headerName: "Avg. IGO to Delivered",
              type: "rightAligned",
              valueFormatter: metricFormatter,
            },
          ]
        : []),
      {
        field: "avg_lead_time_calendar_days",
        headerName: "Avg. Lead Time",
        type: "rightAligned",
        valueFormatter: metricFormatter,
      },
      {
        field: "rfps_turnaround_under_12_business_days",
        headerName: "TAT Under 12 Days",
        type: "rightAligned",
        valueFormatter: numberFormatter,
      },
      {
        field: "rfps_turnaround_12_to_14_business_days",
        headerName: "TAT 12–14 Days",
        type: "rightAligned",
        valueFormatter: numberFormatter,
      },
      {
        field: "rfps_turnaround_15_plus_business_days",
        headerName: "TAT 15+ Days",
        type: "rightAligned",
        valueFormatter: numberFormatter,
      },
    ],
    [showIgoColumns],
  );

  return (
    <Box data-testid="rfp-submitter-activity-card" sx={CARD_SX}>
      <CardTitle>Centro Broker Portal User Activity</CardTitle>
      <Box sx={{ mt: 2 }}>
        {rows.length === 0 ? (
          <Typography sx={{ py: 2, px: 1 }} color="textSecondary">
            No RFP submitter activity data.
          </Typography>
        ) : (
          <StewardshipTable rowData={rows} columnDefs={columnDefs} />
        )}
      </Box>
    </Box>
  );
}

export { RfpSubmitterActivityCard };
export type { RfpSubmitterActivityCardProps };
